import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { map, find } from 'react-sortable-tree';
import { getActiveSpace } from 'spaces/index';
import { getIsAdmin } from 'auth/store/auth/selectors';
import MenuSkeleton from 'base/containers/Menu/MenuSkeleton';
import FolderTree from '../../components/FolderTree/index';
import {
  getActiveSpaceFolderTreeIsLoading,
  getActiveFolderTree,
  getFoldersById,
  getDocumentCountByFolderId,
  getActiveFolder,
} from '../../store/folders/selectors';
import {
  fetchFolderTree,
  setFolderTree,
  updateFolder,
  updateMultipleFolders,
} from '../../store/folders/actions';
import { getEntities } from '../../../store/entities/selectors';


class SpaceFolderTree extends React.Component {
  componentDidMount() {
    this.fetchData();
    this.autoExpand();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.space.id !== this.props.space.id) {
      this.fetchData();
    }
    if ((!prevProps.activeFolder && this.props.activeFolder)
        || (prevProps.activeFolder && this.props.activeFolder
            && prevProps.activeFolder.id !== this.props.activeFolder.id)) {
      this.autoExpand();
    }
  }

  fetchData = () => this.props.fetchAll(this.props.space.id);

  handleTreeChange = (treeData) => this.props.setFolderTree(this.props.space.id, treeData);

  autoExpand = () => {
    if (!this.props.activeFolder || !this.props.treeData) {
      return;
    }
    const { treeData } = find({
      treeData: this.props.treeData,
      getNodeKey: (n) => n.id,
      searchQuery: this.props.activeFolder.pathIds,
      searchMethod: ({ node, searchQuery }) => searchQuery.includes(node.id),
      expandAllMatchPaths: true,
    });
    this.handleTreeChange(treeData);
  };

  handleMoveNode = ({ node, nextParentNode }) => {
    const { foldersById } = this.props;
    // update node parent (SortableTree uses children, the parent property is thus not updated)
    const folder = foldersById[node.id];
    const updatedFolder = folder;
    updatedFolder.parent = nextParentNode ? nextParentNode.id : null;
    this.props.updateFolder(updatedFolder, false);

    // Update weights of all expanded nodes
    this.updateWeights();
  };

  updateWeights = () => {
    const updatedFolders = [];
    map({
      treeData: this.props.treeData,
      getNodeKey: (n) => n.id,
      callback: ({ node, treeIndex }) => {
        const { foldersById } = this.props;
        const oldFolder = foldersById[node.id];
        const updatedNode = { ...oldFolder, weight: treeIndex };
        const folder = updatedNode;
        if (folder && folder.id && folder.weight !== oldFolder.weight) {
          updatedFolders.push(folder);
        }
        return updatedNode;
      },
    });
    this.props.updateMultipleFolders(updatedFolders);
  };

  canDrag = ({ node }) => this.props.isAdmin || node.canWrite;

  canDrop = ({ nextParent }) => this.props.isAdmin
      || (nextParent ? nextParent.canCreate : this.props.space.defaultFolderPermissions.canCreate);

  render() {
    const {
      treeData, isLoading, documentCountByFolderId, isAdmin, ...other
    } = this.props;
    const folderTree = (
      <FolderTree
        treeData={treeData}
        onChange={this.handleTreeChange}
        onMoveNode={this.handleMoveNode}
        generateNodeProps={() => ({ documentCountByFolderId })}
        canDrag={this.canDrag}
        canDrop={this.canDrop}
        {...other}
      />
    );
    return (
      isLoading ? <MenuSkeleton /> : folderTree
    );
  }
}
SpaceFolderTree.propTypes = {
  space: PropTypes.object.isRequired, // eslint-disable-line
  entities: PropTypes.objectOf(PropTypes.object).isRequired,
  treeData: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchAll: PropTypes.func.isRequired,
  setFolderTree: PropTypes.func.isRequired,
  updateFolder: PropTypes.func.isRequired,
  updateMultipleFolders: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  foldersById: PropTypes.objectOf(PropTypes.object).isRequired,
  documentCountByFolderId: PropTypes.objectOf(PropTypes.any).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  activeFolder: PropTypes.objectOf(PropTypes.any).isRequired,
};

const mapStateToProps = (state) => ({
  isLoading: getActiveSpaceFolderTreeIsLoading(state),
  treeData: getActiveFolderTree(state),
  space: getActiveSpace(state),
  entities: getEntities(state),
  foldersById: getFoldersById(state),
  documentCountByFolderId: getDocumentCountByFolderId(state),
  isAdmin: getIsAdmin(state),
  activeFolder: getActiveFolder(state),
});

const mapDispatchToProps = {
  fetchAll: fetchFolderTree,
  setFolderTree,
  updateFolder,
  updateMultipleFolders,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SpaceFolderTree);
